import type { ReactNode } from "react";
import { Modal } from "../Modal";
import type { Invoice } from "../../services/financialService";
import { daysOverdue, invoiceBilling, invoiceIssuedOn, invoiceStatusLabel, INVOICE_STATUS_DOTS } from "./invoiceRegister";

const money = (value: string | number | undefined | null) => `UGX ${Number(value || 0).toLocaleString()}`;

/**
 * One invoice in full: what it bills, what has been paid or credited, and
 * what is still owed. What can be done with it (apply a receipt, discount,
 * cancel) depends on the page, so it comes in as `children` and `footer`.
 */
export function InvoiceDetailsModal({
  invoice,
  onClose,
  footer,
  children,
}: {
  invoice: Invoice | null;
  onClose: () => void;
  footer?: ReactNode;
  children?: ReactNode;
}) {
  if (!invoice) return null;

  const late = daysOverdue(invoice);
  const minimum = Number(invoice.minimum_payment ?? 0);
  const facts: [string, ReactNode][] = [
    ["Student", invoice.student_name],
    ["Class", invoice.class_name || "—"],
    ["Issued", invoiceIssuedOn(invoice) || "—"],
    [
      "Due",
      <>
        {invoice.due_date}
        {late > 0 && <span className="ml-1.5 font-medium text-red-600">· {late} day{late === 1 ? "" : "s"} overdue</span>}
      </>,
    ],
    ["Billed from", invoiceBilling(invoice)],
    [
      "Payment terms",
      !invoice.allows_partial_payment || (minimum > 0 && minimum >= Number(invoice.balance_due))
        ? "Pay in full"
        : minimum > 0
          ? `Installments, at least ${money(minimum)} first`
          : "Installments allowed",
    ],
  ];
  const totals: [string, string][] = [
    ["Invoice total", money(invoice.total_amount)],
    ["Paid", money(invoice.paid_amount)],
    ["Credited", money(invoice.credited_amount)],
  ];

  return (
    <Modal isOpen size="3xl" title={`Invoice ${invoice.invoice_number}`} onClose={onClose} footer={footer}>
      <div className="space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg bg-primary-50 px-4 py-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-primary-700">Balance due</p>
            <p className="text-2xl font-semibold tabular-nums text-gray-900">{money(invoice.balance_due)}</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-gray-700">
            <span className={`h-1.5 w-1.5 rounded-full ${INVOICE_STATUS_DOTS[invoice.status] ?? "bg-gray-400"}`} />
            {invoiceStatusLabel(invoice.status)}
          </span>
        </div>

        <div className="grid gap-2 sm:grid-cols-3">
          {totals.map(([label, value]) => (
            <div key={label} className="rounded-lg border border-gray-200 px-3 py-2">
              <p className="text-xs text-gray-500">{label}</p>
              <p className="mt-0.5 text-sm font-semibold tabular-nums text-gray-900">{value}</p>
            </div>
          ))}
        </div>

        <dl className="grid gap-x-6 rounded-lg border border-gray-200 px-4 py-1 sm:grid-cols-2">
          {facts.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-4 border-b border-gray-100 py-2 last:border-0">
              <dt className="text-sm text-gray-500">{label}</dt>
              <dd className="break-words text-right text-sm font-medium text-gray-900">{value}</dd>
            </div>
          ))}
        </dl>

        <section aria-labelledby="invoice-lines-heading">
          <h4 id="invoice-lines-heading" className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            What it bills
          </h4>
          {invoice.lines?.length ? (
            <div className="sheet-wrap mt-2 max-h-none">
              <table className="sheet">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="text-right">Qty</th>
                    <th className="text-right">Unit (UGX)</th>
                    <th className="text-right">Total (UGX)</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.lines.map((line) => (
                    <tr key={line.id}>
                      <td className={Number(line.line_total) < 0 ? "text-emerald-700" : "text-gray-900"}>
                        {line.fee_name || line.description}
                        {line.fee_name && line.description && line.description !== line.fee_name && (
                          <span className="block text-[11px] text-gray-500">{line.description}</span>
                        )}
                      </td>
                      <td className="text-right tabular-nums text-gray-600">{line.quantity}</td>
                      <td className="text-right tabular-nums text-gray-600">{Number(line.unit_amount).toLocaleString()}</td>
                      <td className={`text-right tabular-nums ${Number(line.line_total) < 0 ? "text-emerald-700" : "text-gray-900"}`}>
                        {Number(line.line_total).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-1.5 text-sm text-gray-500">A direct invoice for {money(invoice.total_amount)}, without itemised lines.</p>
          )}
        </section>

        {invoice.notes?.trim() && (
          <section aria-labelledby="invoice-notes-heading">
            <h4 id="invoice-notes-heading" className="text-xs font-semibold uppercase tracking-wide text-gray-500">Notes</h4>
            <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-6 text-gray-800">{invoice.notes}</p>
          </section>
        )}

        {invoice.status === "cancelled" && (
          <p className="rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700">
            Cancelled{invoice.cancelled_at ? ` on ${invoice.cancelled_at.slice(0, 10)}` : ""}
            {invoice.cancellation_reason ? `: ${invoice.cancellation_reason}` : "."}
          </p>
        )}

        {children}
      </div>
    </Modal>
  );
}
